/**
 * Lightweight analytics shim — one tiny module the rest of the
 * app calls, so we can swap providers (Sentry breadcrumbs today,
 * GA / Plausible / Mixpanel tomorrow) without hunting through
 * 50 components.
 *
 * Two methods:
 *   - track(eventName, properties?)  → custom event
 *   - pageView(path, title?)         → SPA route change
 *
 * What it does today:
 *   1. Logs to console in dev so it's debuggable
 *   2. Sends a Sentry breadcrumb (visible in the user's last-N
 *      events when an error happens, AND queryable via Sentry's
 *      issue search)
 *
 * Choosing a provider is configuration, not a code change. Set exactly one
 * of these and the loader below picks it up on first import:
 *
 *   VITE_GA_MEASUREMENT_ID   e.g. G-XXXXXXXXXX      → GA4
 *   VITE_PLAUSIBLE_DOMAIN    e.g. get-stacked.com   → Plausible
 *   VITE_POSTHOG_KEY         e.g. phc_xxx           → PostHog
 *                            (+ optional VITE_POSTHOG_HOST)
 *
 * With none of them set nothing loads, no script tag is injected, and the
 * Sentry breadcrumb path below is unchanged — which is the state today.
 * That means this file is finished ahead of the provider decision rather
 * than waiting on it.
 *
 * ⚠ The cookie policy currently states we use no analytics and no
 * cross-site tracking. That sentence becomes FALSE the moment one of these
 * variables is set. Update src/components/privacyPolicyTerms/CookiesData.jsx
 * in the same change — see the master plan, 6.5.
 */

import * as Sentry from "@sentry/react";

const isDev = import.meta.env.MODE === "development";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

/** Which provider, if any, is configured. Exported so a health check or the
 *  admin panel can show what's actually reporting rather than guessing. */
export const analyticsProvider =
  GA_ID ? "ga4" : PLAUSIBLE_DOMAIN ? "plausible" : POSTHOG_KEY ? "posthog" : "none";

let loaded = false;

/** Inject a provider script once, lazily. No-op when nothing is configured. */
function ensureLoaded() {
  if (loaded || typeof document === "undefined") return;
  loaded = true;
  try {
    if (analyticsProvider === "ga4") {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      // anonymize_ip is on because these events are behavioural, never
      // identifying — see sanitizeProps, which drops anything that isn't a
      // flat primitive before it leaves the app.
      window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
    } else if (analyticsProvider === "plausible") {
      const s = document.createElement("script");
      s.defer = true;
      s.setAttribute("data-domain", PLAUSIBLE_DOMAIN);
      s.src = "https://plausible.io/js/script.manual.js";
      document.head.appendChild(s);
      window.plausible = window.plausible || function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
    } else if (analyticsProvider === "posthog") {
      const s = document.createElement("script");
      s.async = true;
      s.src = `${POSTHOG_HOST.replace(/\/$/, "")}/static/array.js`;
      s.onload = () => {
        try { window.posthog?.init(POSTHOG_KEY, { api_host: POSTHOG_HOST, capture_pageview: false }); }
        catch { /* ignore */ }
      };
      document.head.appendChild(s);
    }
  } catch {
    /* never let analytics break the app */
  }
}

/**
 * Keys we never forward to a third-party provider, even though they are
 * useful in a first-party Sentry breadcrumb.
 *
 * GA4 and PostHog both prohibit sending personal or persistent user
 * identifiers as event parameters, and an account id is exactly that. The
 * breadcrumb keeps them — Sentry is first-party and already knows who the
 * user is — and the provider gets the behaviour without the identity.
 */
const PROVIDER_BLOCKED_KEYS = new Set([
  "userId", "user_id", "email", "phone", "name", "ref",
]);

function stripIdentifiers(props) {
  const out = {};
  for (const [k, v] of Object.entries(props || {})) {
    if (PROVIDER_BLOCKED_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/** Send one event to whichever provider is configured. */
function fireToProvider(eventName, rawProps) {
  try {
    ensureLoaded();
    const props = stripIdentifiers(rawProps);
    if (analyticsProvider === "ga4") window.gtag?.("event", eventName, props);
    else if (analyticsProvider === "plausible") window.plausible?.(eventName, { props });
    else if (analyticsProvider === "posthog") window.posthog?.capture?.(eventName, props);
  } catch {
    /* ignore */
  }
}

/**
 * Fire a custom event. Use snake_case names for consistency
 * (matches GA's convention). Properties should be flat
 * primitives — no nested objects, no PII.
 *
 * Examples:
 *   track("demo_started",  { vertical: "realestate", tier: "pro" })
 *   track("tour_step",     { stepIndex: 3 })
 *   track("convert_modal_opened")
 */
export function track(eventName, properties = {}) {
  if (!eventName) return;
  const safeProps = sanitizeProps(properties);

  // Console (dev only) — easy to spot in the console while we
  // iterate on the demo funnel.
  if (isDev) {
    console.log(`[analytics] ${eventName}`, safeProps);
  }

  // Sentry breadcrumb — shows up in the breadcrumb trail of any
  // error that fires later, and is queryable via Sentry issues.
  try {
    Sentry.addBreadcrumb({
      category: "analytics",
      message: eventName,
      data: safeProps,
      level: "info",
    });
  } catch {
    /* ignore — Sentry not initialized */
  }

  fireToProvider(eventName, safeProps);
}

/**
 * Fire a page-view event. Use after a SPA route change.
 *
 * Examples:
 *   pageView("/demo")
 *   pageView("/demo/realestate/pro", "Real Estate Pro tour")
 */
export function pageView(path, title) {
  if (!path) return;
  if (isDev) {
    console.log(`[analytics] page_view ${path}`, title ? `(${title})` : "");
  }
  try {
    Sentry.addBreadcrumb({
      category: "navigation",
      message: `page_view ${path}`,
      data: title ? { title } : undefined,
      level: "info",
    });
  } catch {
    /* ignore */
  }

  try {
    ensureLoaded();
    if (analyticsProvider === "ga4") {
      window.gtag?.("event", "page_view", { page_path: path, page_title: title });
    } else if (analyticsProvider === "plausible") {
      window.plausible?.("pageview", { u: window.location.origin + path });
    } else if (analyticsProvider === "posthog") {
      window.posthog?.capture?.("$pageview", { $current_url: window.location.origin + path });
    }
  } catch {
    /* ignore */
  }
}

/**
 * Strip anything that could be PII or a nested object before
 * we send it to a third party. Whitelist: string / number /
 * boolean. Object / array / function get JSON.stringified to
 * a short summary so the event isn't lost entirely.
 */
function sanitizeProps(props) {
  const out = {};
  for (const [k, v] of Object.entries(props || {})) {
    if (v == null) continue;
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") {
      out[k] = v;
    } else {
      try {
        const j = JSON.stringify(v);
        out[k] = j.length > 200 ? `${j.slice(0, 200)}…` : j;
      } catch {
        out[k] = "[unserializable]";
      }
    }
  }
  return out;
}
